import React, { useContext, useEffect, useState } from 'react';
import { Box, Stack, Text, LinkButton } from '@tonic-ui/react';
import { useToggle } from '@tonic-ui/react-hooks';
import { useTranslation } from 'react-i18next';
import {
  CheckboxInput,
  FormContainer,
  InputField,
  SubmitButton,
  wsLoginValidation,
} from '../../components/forms';
import { useStorageState } from '../../hooks/useStorageState';
import { confirmWSUser, createWSSession } from '../../services/ds-api';
import { AuthContext } from '../../context/AuthContext';

import WSPasswordResetModal from './ws-password-reset-modal';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import useQueryParams from '../../utils/query-params';
import WSPasswordUpdateModal from './ws-password-update-modal';
import CenteredLayout from '../../components/centered-layout';
import { MaintenanceNoticeBanner } from '../../components/maintenance-notice-banner';

const LegacyWSLoginRescue = () => {
  const { eventDistributor } = useContext(AuthContext);
  const { t } = useTranslation();
  const {
    event = '',
    account: passwordUpdateAccount = '',
    username: passwordUpdateUsername = '',
    code: passwordUpdateCode = '',
  } = useQueryParams<AuthQueryParams>(window.location);

  const [uiPref, setUiPref] = useStorageState('localStorage', 'C1C_UI_PREF', {});
  const [useMfa, setUseMfa] = useState(false);
  const [formErrors, setFormErrors] = useState({
    error: '',
  });

  const [wsPasswordResetModalIsOpen, setWsPasswordResetModalIsOpen] = useToggle(false);
  const [wsPasswordUpdateModalIsOpen, setWsPasswordUpdateModalIsOpen] = useToggle(false);
  const { confirmation = '' } = useQueryParams(window.location);
  const [showV1InactiveAccountError, setShowV1InactiveAccountError] = useState(false);

  useEffect(() => {
    if (confirmation) {
      confirmWSUser(confirmation);
    }
  }, [confirmation]);

  useEffect(() => {
    if (event === 'WSForgotPassword') {
      setWsPasswordUpdateModalIsOpen(true);
    }
  }, [event]);

  const [isV1InactiveAccount, setIsV1InactiveAccount] = useStorageState(
    'localStorage',
    'C1C_V1_INACTIVE_ACCOUNT',
    false
  );

  useEffect(() => {
    if (isV1InactiveAccount) {
      setIsV1InactiveAccount(false);
      setShowV1InactiveAccountError(true);
    }
  }, [isV1InactiveAccount]);

  const handleWsRescueSubmit = async ({
    account,
    username,
    password,
    mfaCode,
    setSubmitting,
    resetForm,
  }) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    // Clear any saved info for WS from local storage
    setUiPref({ ...uiPref, rememberAccountWS: false, wsDetails: {} });

    try {
      const { loggedInUser, error, ...rest } = await createWSSession(
        account,
        username,
        password,
        mfaCode
      );
      if (error) {
        console.error(error);

        setFormErrors({
          ...formErrors,
          error: error.includes('SecurityException')
            ? t('wsLogin.credentialsInvalid')
            : error.includes('503')
            ? t('wsLogin.serviceUnavailable')
            : error,
        });
        setSubmitting(false);
        resetForm();
      }

      if (loggedInUser) {
        const { RID } = loggedInUser;
        sessionStorage.setItem('rID', RID);
        localStorage.setItem('C1C_SSO_V2_ENABLED', false);
        localStorage.setItem('WS_RESCUE_LOGGED_IN_USER', true);
        eventDistributor.dispatch({ type: 'WS_RESCUE_LOGGED_IN' });
      }
    } catch (error) {
      console.error(error);

      setFormErrors({
        ...formErrors,
        error: error.message ? error.message : error,
      });

      setSubmitting(false);
      resetForm();
    }
  };

  return (
    <>
      <MaintenanceNoticeBanner />
      {showV1InactiveAccountError && (
        <NotificationBanner
          name="v1-inactive-account-error"
          severity="error"
          shouldOpen={showV1InactiveAccountError}
          bannerText="notActiveAccountError.message"
          bannerLinkTxt="notActiveAccountError.linkText"
          bannerLink={t('notActiveAccountError.supportLink')}
        />
      )}
      {wsPasswordResetModalIsOpen && (
        <WSPasswordResetModal
          isOpen={wsPasswordResetModalIsOpen}
          onClose={() => setWsPasswordResetModalIsOpen(false)}
        />
      )}
      {wsPasswordUpdateModalIsOpen && (
        <WSPasswordUpdateModal
          account={passwordUpdateAccount}
          username={passwordUpdateUsername}
          code={passwordUpdateCode}
          isOpen={wsPasswordUpdateModalIsOpen}
          onClose={() => setWsPasswordUpdateModalIsOpen(false)}
        />
      )}

      <CenteredLayout>
        <Stack direction="column" spacing="4x">
          <Text size="2xl" lineHeight="xl">
            {t('wsLogin.updateAccountTitle')}
          </Text>
          <Text size="l" lineHeight="l">
            {t('wsLogin.updateAccountInfo')}
          </Text>
          <Text size="l" lineHeight="l">
            {t('wsLogin.updateAccountStepsInfo')}
          </Text>
          <Box>
            {formErrors?.error && (
              <NotificationBanner
                name="ws-login-error"
                severity="error"
                variant="outline"
                shouldOpen={true}
                bannerText={formErrors.error}
              />
            )}
            <FormContainer
              id="ws-login-form"
              customValidation={wsLoginValidation}
              initialValues={{
                account: '',
                username: '',
                password: '',
                mfaCode: '',
                useMFACheck: false,
              }}
              enableReinitialize={true}
              submitAction={(formPayload) => handleWsRescueSubmit(formPayload)}
            >
              <Stack direction="column" spacing="4x" mt="12px">
                <Box>
                  <InputField
                    id="account"
                    type="text"
                    label={t('wsCommon.account')}
                    name="account"
                    isRequired={true}
                  />
                </Box>
                <Box>
                  <InputField
                    id="username"
                    type="text"
                    label={t('wsCommon.username')}
                    name="username"
                    isRequired={true}
                  />
                </Box>
                <Box>
                  <InputField
                    id="password"
                    type="password"
                    label={t('password')}
                    name="password"
                    isRequired={true}
                  />
                </Box>
                <Box>
                  <LinkButton
                    marginTop={5}
                    onClick={() => {
                      setWsPasswordResetModalIsOpen(true);
                    }}
                  >
                    {t('forgotPassword')}
                  </LinkButton>
                </Box>
                <CheckboxInput
                  label={t('wsLogin.useMFA')}
                  name="useMFACheck"
                  onCheckboxChange={(value) => setUseMfa(value)}
                />
                {useMfa && (
                  <Box>
                    <InputField
                      id="ws-mfa-code"
                      type="text"
                      label={t('wsLogin.mfaCode')}
                      name="mfaCode"
                      isRequired={true}
                    />
                  </Box>
                )}
                <SubmitButton label={t('signIn')} id="legacy-ws-login-rescue-submit-btn" />
              </Stack>
            </FormContainer>
          </Box>
        </Stack>
      </CenteredLayout>
    </>
  );
};

export default LegacyWSLoginRescue;
